///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// "Save to cloud" toolbar button for dataset browser windows. Rendered
/// only when the window has an uploadable file — the original dropped
/// file (CSV/GeoJSON) or a GeoJSON conversion (shapefile) — and the user
/// is signed in. Uploads directly to B2 with a progress ring and settles
/// into a check (saved) or an error tooltip.
///

import { getDatasetSourceFile } from "@/components/features/user-datasets/datasetSourceFiles"
import { updateDatasetWindow } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { useUploadUserDatasetMutation } from "@/redux/userDatasetsApi"
import { cn } from "@/utils/utils"
import type { UserDatasetRecord } from "@shared-types"
import { AlertCircle, Check, CloudUpload } from "lucide-react"
import { useState } from "react"

type UploadState =
	| { phase: "idle" }
	| { phase: "uploading"; fraction: number }
	| { phase: "done" }
	| { phase: "error"; message: string }

export function SaveToCloudButton({ windowId, title }: { windowId: string; title: string }) {
	const dispatch = useAppDispatch()
	const signedIn = useAppSelector(state => !!state.user.userProfile)
	// Image overlays store their georeference with the upload
	const imageBounds = useAppSelector(state => state.dialog.datasetWindows[windowId]?.imageOverlay?.bounds)
	const [uploadDataset] = useUploadUserDatasetMutation()
	const [state, setState] = useState<UploadState>({ phase: "idle" })

	const source = getDatasetSourceFile(windowId)
	if (!signedIn || !source) {
		return null
	}

	const upload = async () => {
		if (state.phase === "uploading" || state.phase === "done") return
		setState({ phase: "uploading", fraction: 0 })
		const result = await uploadDataset({
			file: source.file,
			name: title,
			format: source.format,
			featureCount: source.featureCount,
			bounds: source.format === "image" ? imageBounds : undefined,
			onProgress: fraction => setState({ phase: "uploading", fraction }),
		})
		if ("error" in result && result.error) {
			const error = result.error as { data?: { message?: string }; error?: string }
			setState({ phase: "error", message: error.data?.message ?? error.error ?? "Upload failed" })
		} else {
			// Link the window to its cloud identity so story capture can
			// reference this dataset instead of embedding it
			const record = (result as { data: UserDatasetRecord }).data
			dispatch(updateDatasetWindow({
				id: windowId,
				updates: {
					cloudDataset: {
						id: record.id,
						name: record.name,
						filename: record.filename,
						format: record.format,
						cdnUrl: record.cdn_url,
						featureCount: record.feature_count ?? undefined,
					},
				},
			}))
			setState({ phase: "done" })
		}
	}

	const label = state.phase === "done"
		? "Saved to your cloud storage"
		: state.phase === "uploading"
			? `Uploading… ${Math.round(state.fraction * 100)}%`
			: state.phase === "error"
				? `Upload failed: ${state.message} — click to retry`
				: "Save to cloud storage"

	return (
		<button
			onClick={() => { void upload() }}
			disabled={state.phase === "uploading" || state.phase === "done"}
			title={label}
			aria-label={label}
			className={cn(
				"p-2 rounded-lg transition-all duration-300 group",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50",
				state.phase === "done"
					? "bg-emerald-500/10 border border-emerald-500/30"
					: "bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 hover:border-cyan-400/50"
			)}
		>
			{state.phase === "done" ? (
				<Check className="w-4 h-4 text-emerald-400" />
			) : state.phase === "error" ? (
				<AlertCircle className="w-4 h-4 text-red-400" />
			) : state.phase === "uploading" ? (
				<span className="block w-4 h-4 text-[9px] leading-4 text-cyan-300 text-center tabular-nums">
					{Math.round(state.fraction * 100)}
				</span>
			) : (
				<CloudUpload className="w-4 h-4 text-cyan-400" />
			)}
		</button>
	)
}
